/**
 * Точка входа клиента. Оболочка (C2, `app/`): язык, стор, маршрут, слоты HUD/окон/вкладок, Telegram WebApp, пауза в
 * фоне, вход, цикл кадров. Поле (C1, `field/field.ts`) рисует фейковый поток боя атласом (заглушка + воксельные модели
 * A1). Бой — настоящий engine (I1, `game/`); HUD (C3), окна (C4), вкладки (C5) подключаются `register*` из `./app`.
 *
 * Отладочные параметры адреса (только локальный хост, `app/debug-params.ts`): `?mock=1` — API в памяти; `?fake=1` — фейковый бой вместо engine (`?seed=N`, `?wave=N`, `?auto=0`); `?speed=N` — ускорение боя (только e2e); `?bench=N` — эталонная сцена замера
 * рендера (C1); `?pause=N` — остановить бой на тике N (скриншоты, e2e); `?auto=0` — навыки сами не срабатывают
 * (e2e HUD: тап по готовому навыку).
 */
import { createFakeBattleStream } from "@newfungame/engine";
import { defaultTransport } from "./api";
import { detectMode, fetchDefaultMode, getGameMode, setGameMode } from "./app/mode";
import { readLaunch, sessionStore } from "./app/launch";
import type { FieldScale, PlayerCommand, WaveLog } from "@newfungame/contracts";
import {
  appStore,
  battleSpeed,
  createFrameLoop,
  registerDesktop,
  registerHud,
  registerTabs,
  signIn,
  startApp,
  type SnapshotSource,
} from "./app";
import { analytics, startAnalytics } from "./analytics";
import { playSound, startAudio } from "./audio";
import { connectGame } from "./game";
import { retrySignIn } from "./sync";
import { chibiAtlases, FIRST_ATLASES, loadAtlas } from "./field/atlas";
import { createBenchStream } from "./field/bench";
import { createField, type Field, type FieldDebug, type FieldStats } from "./field/field";
import { Desktop } from "./ui/desktop";
import { connectHud, Hud } from "./ui/hud";
import { debugParams } from "./app/debug-params";
import { createHeroIntro } from "./app/hero-intro";
import { currentFieldScale, rememberFieldScale } from "./app/field-scale";
import { FIELD_LOOKS } from "./field/scale";
import { setupAsks } from "./app/asks";
import { setupPayments } from "./app/payments";
import { setupShare } from "./app/share";
import { setupTelegramLogin } from "./app/login";
import { registerWindows, setWindowActions } from "./ui/windows";
import { Tabs } from "./ui/tabs";
import { watchPlayerId } from "./watch/watch";
import { chibiIconRenderer, setIconRenderer } from "./ui/kit";

declare global {
  interface Window {
    /** Отладка и e2e: номер последнего тика, экранные точки поля и счётчики рендера. */
    __nfg?: { frame: number; wave: number; field?: FieldDebug; stats?: FieldStats; scale?: FieldScale };
    __nfgField?: Field;
    /** Журналы завершённых волн (e2e; I2 отправляет их на сервер). */
    __nfgWaveLogs?: WaveLog[];
  }
}

async function main(): Promise<void> {
  // зритель (MADR 0040): страницу отдаёт сервер только владельцу, игра здесь не поднимается
  const watched = watchPlayerId(location.pathname);
  if (watched) {
    const { watchMain } = await import("./watch/watch");
    return watchMain(watched);
  }
  // оболочка — первой: язык, отступы и вход не ждут загрузки атласа
  const params = debugParams(location.search, location.hostname);
  setIconRenderer(chibiIconRenderer);
  registerHud(Hud);
  registerWindows({ debug: params.get("mock") === "1" });
  registerTabs(Tabs);
  registerDesktop(Desktop);
  const speed = Number(params.get("speed") ?? 1) || 1;
  const mock = params.get("mock") === "1" || import.meta.env.VITE_API_MOCK === "1";
  // бета: режим по пути /beta или startapp=beta, дальше он едет заголовком в каждом запросе
  // на стенде beta.newfungame.com сервер по умолчанию открывает бету (env DEFAULT_GAME_MODE → /config)
  const fallbackMode = mock ? "main" : await fetchDefaultMode();
  setGameMode(
    detectMode(
      location.pathname,
      readLaunch({
        hash: location.hash,
        search: location.search,
        bootInitData: window.__nfgInitData ?? null,
        storage: sessionStore(),
      }).startParam,
      fallbackMode,
    ),
  );
  const api = defaultTransport(params, getGameMode);
  const shell = startApp({ api, root: document.getElementById("ui")! });
  setupShare(api);
  setupPayments(api, shell.telegram);
  if (getGameMode() === "beta") {
    // код беты — отдельным чанком: основная игра его не грузит
    void import("./beta").then((m) =>
      m.startBeta({ api, telegram: shell.telegram, starsDebug: params.get("stars") === "1" }),
    );
  }
  const asks = setupAsks(api);
  setWindowActions({ changeLocale: shell.changeLocale, sendFeedback: shell.sendFeedback });
  startAnalytics(); // события в Umami; без id сайта молчит
  void setupTelegramLogin(api, shell.launch.miniApp); // кнопка входа на сайте — только если сервер знает имя бота
  const canvas = document.getElementById("field") as HTMLCanvasElement;
  // воксельные модели покрывают все сущности v1; атлас-заглушка в первой загрузке не нужен.
  // Чиби-атласы — после первого кадра (MADR 0017): до них сущность рисуется вокселем.
  // Боссы — отдельным атласом `voxel-boss` тоже после первого кадра (MADR 0022): до него босс не виден.
  // Исключения, нужны с первого кадра: `arena` — площадь под полем (ISSUE-87), `chibi-base` — штаб, турель, платформы
  // (ISSUE-86), их вокселей в атласе нет
  const atlas = await loadAtlas("/assets/atlas/", ...FIRST_ATLASES);
  const field = await createField(canvas, atlas, { background: "#5a9e35", coinTarget: coinCounter(canvas) });
  shell.attachField(field);
  // рука A/B-теста масштаба поля (MADR 0036): из профиля, до входа — с прошлого раза
  let fieldScale: FieldScale = "a";
  const applyFieldScale = (): void => {
    const player = appStore.get().player;
    fieldScale = currentFieldScale(player, params.get("scale"), localStore());
    if (player) rememberFieldScale(player.player.fieldScale, localStore());
    field.setLook(FIELD_LOOKS[fieldScale]);
  };
  applyFieldScale();
  appStore.subscribe((s, prev) => {
    if (s.player !== prev.player) applyFieldScale();
  });

  const seed = Number(params.get("seed") ?? 1);
  const bench = params.get("bench");
  const autoCast = params.get("auto") !== "0";
  const fake = params.has("fake") || bench !== null;
  const newStream = (wave: number): SnapshotSource & { command?(cmd: PlayerCommand): void } =>
    bench ? createBenchStream(Number(bench) || 150, seed) : createFakeBattleStream(seed, { startWave: wave, autoCast });
  // по умолчанию — настоящий engine (I1, `game/`); фейковый поток — `?fake=1` и `?bench=N` (e2e поля, HUD и окон)
  let stream: SnapshotSource & { command?(cmd: PlayerCommand): void };
  if (fake) {
    stream = newStream(Number(params.get("wave") ?? 1));
    // сервер закрыл забежку (10 мин в фоне) — новая с волны из player.run
    let epoch = appStore.get().runEpoch;
    appStore.subscribe((s) => {
      if (s.runEpoch === epoch) return;
      epoch = s.runEpoch;
      stream = newStream(s.player?.run.wave ?? 1);
    });
    // команды HUD (навык, «Автонавыки») — в текущий поток фейкового боя
    connectHud((cmd) => stream.command?.(cmd));
  } else {
    // журнал, покупки и клеймы уходят на сервер (sync/); `__nfgWaveLogs` — копия для e2e
    const game = connectGame({
      api,
      ...(mock || speed > 1 ? { pace: 0 } : {}),
      onWaveCleared: asks.waveCleared,
      onWaveLog: (log) => {
        window.__nfgWaveLogs?.push(log);
        analytics().waveLogged(log);
      },
    });
    stream = game;
    // вход не удался (нет сети, сервер недоступен) — бой идёт, вход повторяется
    const auth = retrySignIn({
      status: () => appStore.get().auth.status,
      subscribe: (fn) => appStore.subscribe(fn),
      signIn: () => signIn(api, shell.launch, appStore.get().locale),
    });
    window.addEventListener("online", () => auth.online());
  }

  const intro = createHeroIntro({
    field,
    ready: () => document.body.dataset.chibi === "1",
    speed: () => speed * battleSpeed(appStore.get()),
    reducedMotion: window.matchMedia("(prefers-reduced-motion: reduce)").matches,
    storage: localStore(),
    onBeat: () => playSound("skill_cast", { gain: INTRO_SOUND_GAIN }),
  });
  window.addEventListener("pointerdown", () => intro.stop(), true);

  let firstFrame = false;
  const loop = createFrameLoop({
    source: () => stream,
    speed: () => speed * battleSpeed(appStore.get()),
    isRunning: shell.isBattleRunning,
    pauseAt: params.has("pause") ? Number(params.get("pause")) : Infinity,
    onTick: (s) => {
      shell.pushSnapshot(s);
      intro.onTick(s);
      window.__nfg = {
        frame: s.frame,
        wave: s.wave.number,
        field: field.debug(),
        stats: field.stats(),
        scale: fieldScale,
      };
    },
    onFrame: (s, alpha) => {
      field.render(s, alpha);
      if (!firstFrame) {
        firstFrame = true;
        // метка «первый кадр боя» для бюджета старта (e2e/budgets.spec.ts); звук грузим уже после неё — не в первой загрузке
        performance.mark("nfg-first-frame");
        void startAudio(); // звук: события боя, музыка, пауза; кнопка «без звука» — в оболочке
        const boss = loadAtlas("/assets/atlas/", "voxel-boss")
          .then((bossAtlas) => {
            field.addAtlas(bossAtlas);
            document.body.dataset.boss = "1"; // e2e: кадры боссов загружены
          })
          .catch((e: unknown) => console.warn("boss atlas:", e));
        // грузятся параллельно, но чиби кладём после `voxel-boss`: поздний атлас перекрывает кадры, а чиби-боссы
        // (ISSUE-109) заменяют воксельных
        Promise.all([loadAtlas("/assets/atlas/", ...chibiAtlases(getGameMode())), boss])
          .then(([chibi]) => {
            field.addAtlas(chibi);
            document.body.dataset.chibi = "1"; // e2e: чиби-кадры на поле
          })
          .catch((e: unknown) => console.warn("chibi atlas:", e)); // без чиби бой идёт на вокселях
      }
    },
  });
  window.__nfgWaveLogs = [];
  loop.start();
  window.__nfgField = field;
  document.body.dataset.ready = "1";
}

/** Перекличка героев звучит тише настоящего навыка. */
const INTRO_SOUND_GAIN = 0.45;

function localStore(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Куда летят монеты: иконка счётчика монет HUD в координатах поля. Позиция кешируется на размер экрана — без чтения
 * раскладки каждый кадр; пока HUD не показан — середина верха.
 */
function coinCounter(canvas: HTMLCanvasElement): (width: number, height: number) => { x: number; y: number } {
  let key = "";
  let at = { x: 0, y: 0 };
  return (width, height) => {
    const k = `${width}x${height}`;
    if (k === key) return at;
    const icon = document.querySelector('[data-testid="hud-coins"] .hud-coins__icon');
    if (!icon) return { x: width / 2, y: 24 };
    const r = icon.getBoundingClientRect();
    const c = canvas.getBoundingClientRect();
    key = k;
    at = { x: r.left + r.width / 2 - c.left, y: r.top + r.height / 2 - c.top };
    return at;
  };
}

main().catch((e: unknown) => {
  console.error("[newfungame] start failed", e);
  document.body.dataset.ready = "error";
});
